// --------------------------------------------------------------------------
// Partner attribution capture (WO-4)
//
// Partner links land users on any page with ?partner=<slug> (e.g. ISL's
// "Connect Peekop" card links with ?partner=isl). We stash the slug in
// localStorage so it survives navigation between the landing surface and
// /sign-in, then forward it in Supabase auth options.data as
// `partner_source`. The API's autoProvisionUser reads it from
// user_metadata and stamps organizations.partner_source once at org
// creation — see apps/api/src/middleware/auth.ts.
//
// Attribution is best-effort by design: invalid slugs are dropped, storage
// failures are swallowed, and nothing here ever blocks auth.
// --------------------------------------------------------------------------

export const PARTNER_STORAGE_KEY = "peekop_partner_source";

// Mirrors normalizePartnerSource in apps/api/src/middleware/auth.ts:
// 1–50 chars of [a-z0-9-], must start alphanumeric, stored lowercase.
const PARTNER_SLUG_PATTERN = /^[a-z0-9][a-z0-9-]{0,49}$/;

export function normalizePartnerSlug(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const slug = value.trim().toLowerCase();
  return PARTNER_SLUG_PATTERN.test(slug) ? slug : null;
}

/** Extract a valid partner slug from a query string, or null. */
export function capturePartnerFromQuery(search: string): string | null {
  try {
    const params = new URLSearchParams(search);
    return normalizePartnerSlug(params.get("partner"));
  } catch {
    return null;
  }
}

/**
 * Persist a captured partner slug. First capture wins — a user who arrived
 * via ?partner=isl and later browses a generic marketing link keeps their
 * original attribution.
 */
export function writePendingPartnerSource(
  storage: Pick<Storage, "getItem" | "setItem">,
  slug: string,
): void {
  const normalized = normalizePartnerSlug(slug);
  if (!normalized) return;
  if (normalizePartnerSlug(storage.getItem(PARTNER_STORAGE_KEY))) return; // first capture wins
  storage.setItem(PARTNER_STORAGE_KEY, normalized);
}

/** Read the pending partner slug, or null if absent/invalid. */
export function readPendingPartnerSource(
  storage: Pick<Storage, "getItem">,
): string | null {
  return normalizePartnerSlug(storage.getItem(PARTNER_STORAGE_KEY));
}

/**
 * Convenience for auth call sites: capture from the current URL (if
 * present) and return whatever attribution is pending. Never throws.
 */
export function captureAndReadPartnerSource(
  search: string,
  storage: Pick<Storage, "getItem" | "setItem">,
): string | null {
  try {
    const fromQuery = capturePartnerFromQuery(search);
    if (fromQuery) writePendingPartnerSource(storage, fromQuery);
    return readPendingPartnerSource(storage);
  } catch {
    return null;
  }
}
